// shared.js — Primitivas visuales compartidas del panel /admin/users (#1416).
//
// Extraídas SIN CAMBIO DE LÓGICA de la `UsersAdmin.js` monolítica (design.md,
// tabla "Archivos a tocar · Fase 2"): pills de estado, el avatar, los chips de
// módulos activos, el switch compacto y los bloques `Section`/`RowToggle` que
// arma cada sección del drawer. Las tabs (Uso/Datos/Permisos), el drawer y los
// diálogos importan de acá en vez de redeclarar cada uno su propia paleta.
//
// R35 (colores por token): los dos únicos `rgba(31,111,151,…)` que tenía el
// archivo viejo (ModuleChips y el default de RowToggle) pasan a
// `rgba(var(--color-primary-rgb),…)` — MISMO valor (31,111,151 es
// `--color-primary-rgb` en `theme.js`, dark y light), cero cambio visual.
import {
  Avatar,
  Box,
  Chip,
  Divider,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import { MODULE_FLAGS, PROFESSION_LABELS } from "../../../helpers/admin-users-api";
import { etiquetaEtapa, tonoEtapa } from "../../../helpers/etapas";
import { fechaChile, horaChile } from "../../../helpers/trial-fecha";

export const PALETTE = {
  bgPage: "var(--bg-base)",
  bgElevated: "var(--bg-elevated)",
  // Escalera de elevación, la misma que el resto de la app:
  //   página (--bg-base) → tarjeta (--bg-elevated) → caja interna (--bg-input).
  // Antes la tarjeta usaba `--bg-elevated-alt`, un gris neutro que este panel
  // era el ÚNICO archivo de src/ en referenciar: cortaba con el azulado del
  // resto y en light valía lo mismo que --bg-base, así que las tarjetas
  // directamente no se veían.
  bgCard: "var(--bg-elevated)",
  bgInput: "var(--bg-input)",
  accent: "var(--color-primary)",
  accentSoft: "var(--color-info)",
  success: "var(--color-success)",
  danger: "var(--color-danger)",
  warning: "var(--color-warning)",
  textMuted: "var(--text-muted)",
  textFaint: "var(--text-faint)",
  borderSoft: "rgba(var(--fg-rgb),0.08)",
  borderMedium: "rgba(var(--fg-rgb),0.18)",
};

export const initialsFor = (user) => {
  const name = user.full_name || user.username || user.email || "";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (parts[0]?.slice(0, 2) || "??").toUpperCase();
};

// Cada entrada lleva el color Y su triplet: estos chips pintan fondo y borde
// con opacidad, y `${color}1f` no sirve cuando `color` es un `var(...)` —
// produce "var(--color-success)1f", CSS inválido, y el chip queda sin fondo.
export const VERIFICATION_PILL = {
  APPROVED: { color: PALETTE.success, rgb: "--color-success-rgb", label: "✓ Aprobado" },
  PENDING: { color: PALETTE.warning, rgb: "--color-warning-rgb", label: "⏳ Pendiente" },
  REJECTED: { color: PALETTE.danger, rgb: "--color-danger-rgb", label: "✗ Rechazado" },
  NOT_SUBMITTED: { color: PALETTE.textMuted, rgb: "--text-muted-rgb", label: "○ No enviado" },
};

export const PROFESSION_COLOR = {
  DOCTOR: { color: PALETTE.accentSoft, rgb: "--color-info-rgb" },
  INTERNO: { color: "var(--accent-amber)", rgb: "--accent-amber-rgb" },
  KINESIOLOGIST: { color: "var(--accent-violet-light)", rgb: "--accent-violet-light-rgb" },
  NURSE: { color: "var(--accent-cyan)", rgb: "--accent-cyan-rgb" },
  ESTUDIANTE: { color: PALETTE.success, rgb: "--color-success-rgb" },
  TENS: { color: "var(--accent-cyan)", rgb: "--accent-cyan-rgb" },
  NO_SALUD: { color: PALETTE.textMuted, rgb: "--text-muted-rgb" },
  OTHER: { color: PALETTE.textMuted, rgb: "--text-muted-rgb" },
};

export const VerificationPill = ({ status }) => {
  const cfg = VERIFICATION_PILL[status] || VERIFICATION_PILL.NOT_SUBMITTED;
  return (
    <Chip
      label={cfg.label}
      size="small"
      sx={{
        bgcolor: `rgba(var(${cfg.rgb}),0.12)`,
        color: cfg.color,
        border: `1px solid rgba(var(${cfg.rgb}),0.25)`,
        height: 22,
        fontSize: 11,
        fontWeight: 600,
      }}
    />
  );
};

// Estado de la prueba gratuita. Existe porque desde el alta self-service TODA
// cuenta nueva nace con fecha de corte: dejó de ser una rareza de los internos
// y pasó a ser el estado por defecto. Sin esta columna, el panel decía
// "Aprobado ✓" sin ninguna señal de que esa cuenta tiene cuenta regresiva, y la
// única forma de verlo venir era una query a la base.
// Tope de días sin generar nada desde la concesión (#1133, v4, R47/R50).
// Hardcoded en el front, mismo criterio que los umbrales de `trialState`
// (settings.TRIAL_GRANT_CAP_DAYS en el backend, sin endpoint que lo exponga).
// Portado desde el UsersAdmin.js monolítico al integrar #1416.
export const TRIAL_GRANT_CAP_DAYS = 30;

export const trialState = (trialEndsAt, trialGrantedAt) => {
  if (!trialEndsAt) {
    // v4: "demo sin empezar" (concedida, sin reloj) es distinguible de
    // "permanente" (ni concedida ni arrancada) — R50. La cuenta regresiva
    // que se muestra acá es el TOPE (operativo, para el admin), no una
    // promesa al médico: la tarjeta de R20 no cambia (design.md).
    if (trialGrantedAt) {
      const tope = new Date(new Date(trialGrantedAt).getTime() + TRIAL_GRANT_CAP_DAYS * 86400000);
      const cuando = tope.toLocaleDateString("es-CL");
      return {
        label: "Sin empezar",
        color: PALETTE.textMuted,
        rgb: "--text-muted-rgb",
        title: `Concedida, sin generar nada. Arranca sola antes del ${cuando} si nadie genera un documento.`,
        testId: "admin-demo-sin-empezar",
      };
    }
    return { label: "—", color: PALETTE.textFaint, rgb: "--text-muted-rgb", title: "Sin vencimiento" };
  }
  const vence = new Date(trialEndsAt);
  const cuando = vence.toLocaleDateString("es-CL");
  // Vencida se decide comparando el INSTANTE, no los días redondeados. Con
  // `Math.ceil` sobre la diferencia, las primeras 24 h después de vencer caían
  // en la rama de "le quedan días" y se pintaban ámbar "0 d" — o sea "lo veo
  // mañana" — cuando ese médico ya está recibiendo 403 en el Turno,
  // posiblemente en pleno turno. Es exactamente la persona que esta columna
  // existe para pescar.
  if (vence <= Date.now()) {
    return { label: "Vencida", color: PALETTE.danger, rgb: "--color-danger-rgb", title: `Venció el ${cuando}` };
  }
  const dias = Math.ceil((vence - Date.now()) / 86400000);
  // Tres días o menos es cuando conviene actuar: alcanza para revisar los
  // documentos antes de que al médico se le corte el Turno en pleno turno.
  const urgente = dias <= 3;
  return {
    label: `${dias} d`,
    color: urgente ? PALETTE.warning : PALETTE.textMuted,
    rgb: urgente ? "--color-warning-rgb" : "--text-muted-rgb",
    title: `Vence el ${cuando}`,
  };
};

// Planes exentos de la demo (#1133, misma lista de `billing.access.PLANES_EXENTOS`
// del backend: interno/tecnica nunca son "demo" para access_state).
export const PLANES_EXENTOS_DEMO = ["interno", "tecnica"];

// Estado de demo para el FILTRO del panel (#1133, R19/R36). Mismo umbral que
// `trialState` (3 días o menos es "por vencer"), pero como clave estable en
// vez de un label: el filtro compara contra un valor fijo, no contra texto
// que puede cambiar de idioma o de formato de fecha.
//
// R36 (v2): recibe el USUARIO completo, no solo `trial_ends_at`. Una cuenta
// exenta (plan interno/tecnica), superuser o sin acceso puede tener una
// fecha de corte vieja (quedó de una demo previa, o nunca se limpió) sin
// que eso signifique que HOY está "en demo": `access_state` la clasifica
// como "exenta" o corta el acceso por otra vía, nunca "demo". Clasificarla
// por la fecha sola la hacía aparecer como Vigente/Por vencer/Vencida
// cuando no correspondía.
export const demoEstadoDe = (user) => {
  const trialEndsAt = user?.trial_ends_at;
  if (
    user?.is_superuser ||
    PLANES_EXENTOS_DEMO.includes(user?.plan) ||
    user?.has_access === false
  ) {
    return "no_aplica";
  }
  if (!trialEndsAt) {
    // v4 (#1133, R50): "sin empezar" (concedida, `trial_granted_at` fijado,
    // sin reloj visible, con tope de 30 d) es un estado DISTINTO de "sin
    // fecha" (acceso permanente, ni concedida ni arrancada). La diferencia
    // solo vive en el campo nuevo.
    return user?.trial_granted_at ? "sin_empezar" : "sin_fecha";
  }
  // Se compara el INSTANTE, no el día redondeado (regla dura de
  // frontend.md): un "0 d" con Math.ceil sigue vigente 24 h después de
  // vencer si se compara por día de calendario.
  if (new Date(trialEndsAt) <= Date.now()) return "vencida";
  const dias = Math.ceil((new Date(trialEndsAt) - Date.now()) / 86400000);
  return dias <= 3 ? "por_vencer" : "vigente";
};

export const DEMO_ESTADO_LABELS = {
  vigente: "Vigente",
  por_vencer: "Por vencer (≤3 d)",
  vencida: "Vencida",
  sin_empezar: "Sin empezar (tope 30 d)",
  sin_fecha: "Sin fecha (permanente)",
  no_aplica: "No aplica (exenta o sin acceso)",
};

// Estado del lead frente al CRM (#1466, R11-R14, R25). Manda el CRM del Mini
// (`crm_congelado_desde`, solo lectura, R1/R22): SmartDx nunca decide acá, solo
// clasifica lo que ya llegó por el sync (R4-R10). Mismo patrón que
// `demoEstadoDe`: una clave estable, no un label, para que el filtro
// (`UsersAdmin.js`, `admin-filtro-leads-estado`) compare contra un valor fijo.
export const leadsEstadoDe = (user) => (user?.crm_congelado_desde ? "congelado" : "activo");

export const LEADS_ESTADO_LABELS = {
  activo: "Leads: activos",
  congelado: "Leads: congelados",
};

// ---------------------------------------------------------------------------
// Pausa por inactividad (#1583, R29). Sección 5 del mockup
// (tools/mockups/smartdx_mockups_pausa_inactividad.html), aprobada 09-oct.
// Cubre R29 — .claude/specs/1583-promo-octubre/
//
// El back manda `pausa_inactividad` {estado, pausada_desde, ultima_solicitud_at,
// solicitudes} por cuenta (R22). Solo `pausada`, `revisando` y `reactivada`
// llevan chip: `activa` y `no_aplica` (o un back viejo sin la clave) no muestran
// nada. Un chip más bajo el nombre, al molde de TrialPill y CongeladoChip.
export const pausaEstadoDe = (user) => {
  const estado = user?.pausa_inactividad?.estado;
  return estado === "pausada" || estado === "revisando" || estado === "reactivada" ? estado : "";
};

const PAUSA_TONO = {
  pausada: { rgb: "--color-warning-rgb", color: PALETTE.warning },
  revisando: { rgb: "--color-info-rgb", color: "var(--color-info)" },
  reactivada: { rgb: "--color-success-rgb", color: PALETTE.success },
};

const MINUTOS_REVISION = 3;
const DIAS_VENTANA = 7;

// La ventana de una cuenta `reactivada` nace de la solicitud + 3 min y dura 7
// días (R17): por eso el "hasta" se calcula desde `ultima_solicitud_at`, sin un
// campo aparte en el contrato de `/admin/users`.
const reactivadaDesdeHasta = (pausa) => {
  if (!pausa?.ultima_solicitud_at) return null;
  const desde = new Date(new Date(pausa.ultima_solicitud_at).getTime() + MINUTOS_REVISION * 60000);
  const hasta = new Date(desde.getTime() + DIAS_VENTANA * 86400000);
  return { desde: desde.toISOString(), hasta: hasta.toISOString() };
};

export const pausaChipTexto = (user) => {
  const estado = pausaEstadoDe(user);
  const p = user?.pausa_inactividad;
  if (estado === "pausada") {
    return p.pausada_desde ? `En pausa · desde ${fechaCortaCL(p.pausada_desde)}` : "En pausa";
  }
  if (estado === "revisando") return "Revisando";
  if (estado === "reactivada") {
    const v = reactivadaDesdeHasta(p);
    return v ? `Reactivada · 7 días hasta ${fechaCortaCL(v.hasta)}` : "Reactivada";
  }
  return "";
};

export const fechaHoraCortaCL = (iso) => `${fechaCortaCL(iso)} ${horaChile(iso)}`;

export const pausaChipTooltip = (user) => {
  const estado = pausaEstadoDe(user);
  const p = user?.pausa_inactividad;
  if (!estado) return "";
  const n = `Solicitudes de 7 días más: ${p.solicitudes ?? 0}.`;
  if (estado === "pausada") {
    const desde = p.pausada_desde ? ` (desde el ${fechaCortaConAnioCL(p.pausada_desde)})` : "";
    return `Pausada por 7 días sin generar documentos${desde}. ${n}`;
  }
  if (estado === "revisando") {
    const cuando = p.ultima_solicitud_at
      ? ` el ${fechaCortaCL(p.ultima_solicitud_at)} a las ${horaChile(p.ultima_solicitud_at)}`
      : "";
    return `Pidió 7 días más${cuando} y aún no se reactiva. ${n}`;
  }
  const v = reactivadaDesdeHasta(p);
  return v
    ? `Reactivada el ${fechaCortaCL(v.desde)} con 7 días más gratis, hasta el ${fechaCortaConAnioCL(v.hasta)}. ${n}`
    : `Reactivada con 7 días más gratis. ${n}`;
};

export const PausaChip = ({ user, "data-testid": testId }) => {
  const estado = pausaEstadoDe(user);
  if (!estado) return null;
  const tono = PAUSA_TONO[estado];
  return (
    <Tooltip title={pausaChipTooltip(user)}>
      <Chip
        data-testid={testId}
        data-estado={estado}
        label={pausaChipTexto(user)}
        size="small"
        sx={{
          bgcolor: `rgba(var(${tono.rgb}),0.12)`,
          color: tono.color,
          border: `1px solid rgba(var(${tono.rgb}),0.25)`,
          height: 22,
          fontSize: 11,
          fontWeight: 600,
        }}
      />
    </Tooltip>
  );
};

const MESES_CORTOS_CL = [
  "ene", "feb", "mar", "abr", "may", "jun",
  "jul", "ago", "sep", "oct", "nov", "dic",
];

// "14-sep" — fecha corta en hora Chile, mismo criterio de zona que
// `trial-fecha.js` (el admin piensa en el día chileno, no en el UTC del
// backend). Copy único (D5 del líder): el mismo texto en las 3 pestañas +
// la cabecera del drawer, nunca un texto por pantalla.
const fechaCortaCL = (iso) => {
  const [, m, d] = fechaChile(iso).split("-").map(Number);
  return `${String(d).padStart(2, "0")}-${MESES_CORTOS_CL[m - 1]}`;
};

// "14-sep-2026" — misma fecha, con año, para el tooltip completo.
const fechaCortaConAnioCL = (iso) => {
  const [y, m, d] = fechaChile(iso).split("-").map(Number);
  return `${String(d).padStart(2, "0")}-${MESES_CORTOS_CL[m - 1]}-${y}`;
};

const diasDesdeCL = (iso) => Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000));

// R11/R14: texto visible del chip, SIEMPRE el mismo (no solo en tooltip, que
// no existe en táctil). "" si no está congelado — el caller decide si
// renderizar el chip.
export const chipCongeladoTexto = (user) => {
  if (!user?.crm_congelado_desde) return "";
  return `Congelado · ${fechaCortaCL(user.crm_congelado_desde)}`;
};

// R14: tooltip con la fecha completa y hace cuántos días.
export const chipCongeladoTooltip = (user) => {
  if (!user?.crm_congelado_desde) return "";
  const dias = diasDesdeCL(user.crm_congelado_desde);
  return `Congelado en el CRM desde ${fechaCortaConAnioCL(user.crm_congelado_desde)} (hace ${dias} día${dias === 1 ? "" : "s"})`;
};

// Chip único, reusado por las 3 pestañas de la lista (Uso, Datos, Permisos —
// desktop y mobile) y por la cabecera del drawer (R11, R14, R26): un solo
// componente evita 4 copias del mismo `sx`. D33 (líder, ronda 2): NEUTRO,
// borde y texto `--text-muted`, ícono ❄ — NUNCA `--color-info`/`accentSoft`,
// que ya es el color de la pastilla "Médico" (`PROFESSION_COLOR.DOCTOR`,
// arriba): con `--color-info` acá, "Médico" y "Congelado" se confundirían en
// la misma fila. Sin ningún control de escritura (R26): es un Chip de solo
// lectura, nunca un botón.
export const CongeladoChip = ({ user, "data-testid": testId }) => {
  if (!user?.crm_congelado_desde) return null;
  return (
    <Tooltip title={chipCongeladoTooltip(user)}>
      <Chip
        data-testid={testId}
        icon={<span style={{ fontSize: 11, lineHeight: 1 }}>❄</span>}
        label={chipCongeladoTexto(user)}
        size="small"
        sx={{
          bgcolor: "rgba(var(--fg-rgb),0.05)",
          color: PALETTE.textMuted,
          border: "1px solid var(--border-strong)",
          height: 22,
          fontSize: 11,
          fontWeight: 600,
          "& .MuiChip-icon": { color: PALETTE.textMuted, marginLeft: "6px" },
        }}
      />
    </Tooltip>
  );
};

export const TrialPill = ({ trialEndsAt, trialGrantedAt }) => {
  const cfg = trialState(trialEndsAt, trialGrantedAt);
  return (
    <Tooltip title={cfg.title}>
      <Chip
        label={cfg.label}
        size="small"
        data-testid={cfg.testId}
        sx={{
          bgcolor: `rgba(var(${cfg.rgb}),0.12)`,
          color: cfg.color,
          border: `1px solid rgba(var(${cfg.rgb}),0.25)`,
          height: 22,
          fontSize: 11,
          fontWeight: 600,
        }}
      />
    </Tooltip>
  );
};

// Estado de la suscripción (#862). Al molde de TrialPill: el mismo lenguaje
// visual de píldora con punto, para que "prueba" y "suscripción" se lean
// igual en el resto del panel.
export const SUSCRIPCION_ESTADO_CFG = {
  activa: { label: "Al día", color: PALETTE.success, rgb: "--color-success-rgb" },
  cancelada: { label: "Cancelada", color: PALETTE.textFaint, rgb: "--text-muted-rgb" },
};

export const SuscripcionPill = ({ status }) => {
  if (!status) {
    return (
      <Chip
        label="Sin suscripción"
        size="small"
        sx={{ bgcolor: "rgba(var(--fg-rgb),0.04)", color: PALETTE.textFaint, height: 22, fontSize: 11 }}
      />
    );
  }
  const cfg = SUSCRIPCION_ESTADO_CFG[status] || {
    label: status,
    color: PALETTE.textFaint,
    rgb: "--text-muted-rgb",
  };
  return (
    <Chip
      label={cfg.label}
      size="small"
      sx={{
        bgcolor: `rgba(var(${cfg.rgb}),0.12)`,
        color: cfg.color,
        border: `1px solid rgba(var(${cfg.rgb}),0.25)`,
        height: 22,
        fontSize: 11,
        fontWeight: 600,
      }}
    />
  );
};

export const ProfessionPill = ({ profession, profession_display }) => {
  if (!profession) {
    return (
      <Chip
        label="—"
        size="small"
        sx={{ bgcolor: "rgba(var(--fg-rgb),0.04)", color: PALETTE.textFaint, height: 22, fontSize: 11 }}
      />
    );
  }
  const cfg = PROFESSION_COLOR[profession] || PROFESSION_COLOR.OTHER;
  return (
    <Chip
      label={profession_display || PROFESSION_LABELS[profession] || profession}
      size="small"
      sx={{
        bgcolor: `rgba(var(${cfg.rgb}),0.12)`,
        color: cfg.color,
        border: `1px solid rgba(var(${cfg.rgb}),0.2)`,
        height: 22,
        fontSize: 11,
        fontWeight: 600,
      }}
    />
  );
};

// #1738 R11: la celda ETAPA. Manda la etapa (con la especialidad al lado, o lo que eligió quien no
// es médico); la profesión es solo el respaldo de las cuentas sin etapa. Nunca "—" con etapa
// guardada: ese era el bug (la celda pintaba `profession`, vacía en staff, becado y otro hasta
// subir papeles). Sin etapa ni profesión devuelve null y la celda pinta su "sin etapa".
// Colores: verde = en formación, ámbar = no médico, azul = el resto (mockup
// `tools/mockups/smartdx_mockups_1738_etapa_otro.html`). Cubre R11 — .claude/specs/1738-etapa-alta/
const TONO_ETAPA = {
  ok: { color: PALETTE.success, rgb: "--color-success-rgb" },
  warn: { color: PALETTE.warning, rgb: "--color-warning-rgb" },
  info: { color: PALETTE.accentSoft, rgb: "--color-info-rgb" },
};
export const EtapaPill = ({ user }) => {
  const texto = etiquetaEtapa(user);
  if (!texto) return null;
  const cfg = TONO_ETAPA[tonoEtapa(user)];
  return (
    <Chip
      label={texto}
      size="small"
      data-testid={`etapa-${user?.user_id ?? ""}`}
      sx={{
        bgcolor: `rgba(var(${cfg.rgb}),0.12)`,
        color: cfg.color,
        border: `1px solid rgba(var(${cfg.rgb}),0.2)`,
        height: 22,
        fontSize: 11,
        fontWeight: 600,
      }}
    />
  );
};

export const compactSwitchSx = {
  width: 38,
  height: 22,
  padding: 0,
  display: "flex",
  "& .MuiSwitch-switchBase": {
    padding: "3px",
    backgroundColor: "transparent !important",
    color: "var(--text-primary)",
    "&:hover": { backgroundColor: "transparent !important" },
    "&.Mui-checked": {
      transform: "translateX(16px)",
      color: "var(--text-primary)",
      backgroundColor: "transparent !important",
      "&:hover": { backgroundColor: "transparent !important" },
      "& + .MuiSwitch-track": {
        backgroundColor: PALETTE.accent,
        opacity: 1,
        border: 0,
      },
    },
    "&.Mui-disabled + .MuiSwitch-track": { opacity: 0.4 },
  },
  "& .MuiSwitch-thumb": {
    width: 16,
    height: 16,
    boxShadow: "none",
    backgroundColor: "var(--text-primary)",
  },
  "& .MuiSwitch-track": {
    borderRadius: 11,
    backgroundColor: "rgba(var(--fg-rgb),0.18)",
    opacity: 1,
    border: 0,
  },
};

export const ToggleCell = ({ checked, disabled, onChange }) => (
  <Switch
    checked={!!checked}
    disabled={disabled}
    onChange={(e) => onChange(e.target.checked)}
    disableRipple
    sx={compactSwitchSx}
  />
);

export const StatBox = ({ label, value, color }) => (
  <Box sx={{ flex: 1, minWidth: 120 }}>
    <Typography sx={{ color: PALETTE.textFaint, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.6 }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: 20, fontWeight: 700, color: color || "var(--text-primary)", mt: 0.5 }}>
      {value}
    </Typography>
  </Box>
);

export const UserAvatar = ({ user, size = 32 }) => (
  <Avatar
    sx={{
      width: size,
      height: size,
      fontSize: size * 0.4,
      fontWeight: 600,
      bgcolor: PALETTE.accent,
      background: `linear-gradient(135deg, ${PALETTE.accent}, ${PALETTE.accentSoft})`,
    }}
  >
    {initialsFor(user)}
  </Avatar>
);

export const ModuleChips = ({ user }) => {
  const active = MODULE_FLAGS.filter((m) => user[m.flag]);
  if (active.length === 0) {
    return (
      <Typography sx={{ fontSize: 11, color: PALETTE.textFaint, fontStyle: "italic" }}>
        Sin módulos habilitados
      </Typography>
    );
  }
  return (
    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
      {MODULE_FLAGS.map((m) => (
        <Box
          key={m.flag}
          sx={{
            width: 22,
            height: 22,
            borderRadius: "6px",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 11,
            bgcolor: user[m.flag] ? "rgba(var(--color-primary-rgb),0.22)" : "transparent",
            border: user[m.flag]
              ? "1px solid rgba(var(--color-primary-rgb),0.4)"
              : `1px dashed ${PALETTE.borderSoft}`,
            color: user[m.flag] ? PALETTE.accentSoft : PALETTE.textFaint,
            opacity: user[m.flag] ? 1 : 0.5,
          }}
          title={m.label}
        >
          {m.icon}
        </Box>
      ))}
    </Box>
  );
};

// R27/R34 (D8, D24 del líder): la cabecera de TODA card del drawer expone su
// pastilla + acción de cabecera dentro de UN contenedor observable en el DOM,
// `data-testid="admin-section-actions-<sectionKey>"` — SIEMPRE que el caller
// declare `sectionKey`, incluso con `rightSlot` vacío (el contenedor existe
// igual, R34 exige el testid en las 6 secciones obligatorias aunque hoy no
// tengan ninguna acción). Sin `sectionKey`, `rightSlot` se sigue renderizando
// suelto (compat con algún caller que todavía no lo declare).
export const Section = ({ title, rightSlot, sectionKey, children }) => (
  <Box
    sx={{
      mb: 1.5,
      bgcolor: PALETTE.bgCard,
      border: `1px solid ${PALETTE.borderSoft}`,
      borderRadius: 2,
      p: 1.5,
    }}
  >
    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1} gap={1} flexWrap="wrap">
      <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, color: PALETTE.textMuted }}>
        {title}
      </Typography>
      {sectionKey ? (
        <Box data-testid={`admin-section-actions-${sectionKey}`} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {rightSlot}
        </Box>
      ) : (
        rightSlot
      )}
    </Stack>
    <Divider sx={{ borderColor: PALETTE.borderSoft, mb: 1 }} />
    {children}
  </Box>
);

// R21: par etiqueta-valor, el mismo patrón de "Datos" (`DataSection.js`)
// reusado por el resumen colapsado de "Acceso y permisos" (D26 del líder).
// Movido acá (antes local de `DataSection.js`, sin exportar) para que
// `AccessSection.js` lo use también sin redeclararlo.
export const KV = ({ k, children }) => (
  <Stack direction="row" justifyContent="space-between" gap={1} sx={{ py: 0.4, borderBottom: `1px dashed ${PALETTE.borderSoft}`, "&:last-child": { borderBottom: 0 } }}>
    <Typography sx={{ fontSize: 11, color: PALETTE.textMuted }}>{k}</Typography>
    <Typography sx={{ fontSize: 12, fontWeight: 600, textAlign: "right" }}>{children}</Typography>
  </Stack>
);

export const RowToggle = ({ icon, iconBg, iconColor, label, description, checked, onChange, disabled }) => (
  <Stack
    direction="row"
    alignItems="center"
    gap={1.25}
    sx={{
      py: 1,
      borderBottom: `1px dashed ${PALETTE.borderSoft}`,
      "&:last-child": { borderBottom: 0 },
      opacity: disabled ? 0.55 : 1,
    }}
  >
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: "8px",
        bgcolor: iconBg || "rgba(var(--color-primary-rgb),0.22)",
        color: iconColor || PALETTE.accentSoft,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 14,
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontWeight: 500, fontSize: 13 }}>{label}</Typography>
      <Typography sx={{ color: PALETTE.textFaint, fontSize: 10 }} noWrap>
        {description}
      </Typography>
    </Box>
    <Switch
      checked={!!checked}
      onChange={(e) => onChange(e.target.checked)}
      disabled={disabled}
      disableRipple
      sx={compactSwitchSx}
    />
  </Stack>
);

// ── Semáforo de censo (#1555) ───────────────────────────────────────────────
// Mockup elegido C + franja de contadores de D (`tools/mockups/
// smartdx_mockups_semaforo_censo.html`). Copy, íconos y colores del mockup; los
// colores SOLO por token de `theme.js` (`--color-*` y su `-rgb`).
// Cubre R22/R28 — .claude/specs/1555-semaforo-censo/

export const CENSO_ESTADO = {
  verde: { label: "Le entra", icon: "circle-check", c: "var(--color-success)", rgb: "--color-success-rgb" },
  amarillo: { label: "Parcial", icon: "triangle-alert", c: "var(--color-warning)", rgb: "--color-warning-rgb" },
  rojo: { label: "No le entra", icon: "circle-x", c: "var(--color-danger)", rgb: "--color-danger-rgb" },
  gris: { label: "Nunca pegó", icon: "circle-minus", c: "var(--text-muted)", rgb: "--text-muted-rgb" },
};

export const CENSO_ALERTA = {
  duplico: {
    label: "Duplicó pacientes",
    largo: "Duplicó pacientes al re-pegar",
    icon: "copy",
    c: "var(--accent-violet-light)",
    rgb: "--accent-violet-light-rgb",
  },
  vacio: {
    label: "Vació diagnósticos",
    largo: "Vació diagnósticos",
    icon: "eraser",
    c: "var(--accent-violet-light)",
    rgb: "--accent-violet-light-rgb",
  },
};

// `sin_aplicar` (clase del pegado más reciente, amarillo): se pegó y se leyó, pero no se aplicó.
// Tiene su propia etiqueta: no es "Parcial" (no entró nada).
export const CENSO_SIN_APLICAR = {
  ...CENSO_ESTADO.amarillo,
  label: "Sin aplicar",
  ayuda: "pegó la lista de camas y no la aplicó",
};

// Lo que cada estado significa (el `title` de la pastilla en el mockup).
export const CENSO_AYUDA = {
  verde: "sus pegados se aplican completos",
  amarillo: "entran, pero quedan líneas fuera (RUT roto, cama duplicada)",
  rojo: "pegó y no se aplicó nada, sin un pegado bueno después",
  gris: "pone sus camas a mano",
};

// Códigos cerrados de `errors_sample` (`censo-error-codes.js`) en lenguaje de médico. El código
// crudo queda como fallback: un código nuevo del backend se ve igual, sin romper.
export const CENSO_CODIGO_LABEL = {
  pocas_columnas: "Línea con pocas columnas",
  sin_cama: "No pude detectar la cama",
  doc_invalido: "RUT inválido",
  doc_duplicado: "Documento repetido en el pegado",
  cama_duplicada: "Cama duplicada en el pegado",
  canario_rut: "RUT de prueba",
  cama_fuera_catalogo: "Cama fuera del catálogo de la unidad",
  cama_huerfana: "Cama sin paciente legible",
  identidad_sin_cama: "Paciente sin su línea de cama",
  sin_nombre: "Falta el nombre",
  ia_no_escrito: "La IA trajo un dato que no está en el pegado",
  otro: "Otro motivo",
};
export const censoCodigoLabel = (codigo) => CENSO_CODIGO_LABEL[codigo] || codigo;

// Resultado de aplicar el pegado (`resultado_aplicar`): "" = no se supo ni se apretó Aplicar, `heredada`
// = fila anterior a #1555. Copy del mockup: "No se aplicó" cuando no hay un `ok`.
export const censoResultadoLabel = (resultado, heredada) => {
  if (heredada) return "Sin dato";
  return resultado === "ok" ? "Aplicado" : "No se aplicó";
};

const CENSO_ICONOS = {
  "circle-check": '<circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/>',
  "triangle-alert":
    '<path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"/><path d="M12 9v4"/><path d="M12 17h.01"/>',
  "circle-x": '<circle cx="12" cy="12" r="10"/><path d="m15 9-6 6"/><path d="m9 9 6 6"/>',
  "circle-minus": '<circle cx="12" cy="12" r="10"/><path d="M8 12h8"/>',
  copy:
    '<rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/>',
  eraser:
    '<path d="m7 21-4.3-4.3c-1-1-1-2.5 0-3.4l9.6-9.6c1-1 2.5-1 3.4 0l5.6 5.6c1 1 1 2.5 0 3.4L13 21"/><path d="M22 21H7"/><path d="m5 11 9 9"/>',
  x: '<path d="M18 6 6 18"/><path d="m6 6 12 12"/>',
  "chevron-right": '<path d="m9 18 6-6-6-6"/>',
  "arrow-left": '<path d="m12 19-7-7 7-7"/><path d="M19 12H5"/>',
};

// Íconos lucide del mockup, inline (el repo no trae lucide). `stroke` hereda el color del texto.
export const CensoIcon = ({ name, size = 15 }) => (
  <svg
    viewBox="0 0 24 24"
    aria-hidden="true"
    width={size}
    height={size}
    style={{ stroke: "currentColor", fill: "none", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round", flex: "none" }}
    // Literales estáticos de arriba, nunca datos del servidor.
    dangerouslySetInnerHTML={{ __html: CENSO_ICONOS[name] || "" }}
  />
);

// Pastilla de estado (`.pill` del mockup): ícono + texto, no depende solo del color.
export const CensoEstadoPill = ({ estado, clase, lg = false, testId }) => {
  const e = estado === "amarillo" && clase === "sin_aplicar" ? CENSO_SIN_APLICAR : CENSO_ESTADO[estado] || CENSO_ESTADO.gris;
  return (
    <Box
      component="span"
      data-testid={testId}
      title={e.ayuda || CENSO_AYUDA[estado]}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "5px",
        height: lg ? 30 : 24,
        padding: lg ? "0 13px 0 10px" : "0 10px 0 7px",
        borderRadius: 999,
        fontSize: lg ? 13 : 11.5,
        fontWeight: 700,
        whiteSpace: "nowrap",
        bgcolor: `rgba(var(${e.rgb}),0.12)`,
        color: e.c,
        border: `1px solid rgba(var(${e.rgb}),0.32)`,
      }}
    >
      <CensoIcon name={e.icon} size={lg ? 17 : 15} />
      <span>{e.label}</span>
    </Box>
  );
};

// Chip de alerta violeta (`.al` del mockup).
export const CensoAlertaChip = ({ alerta, corto = false }) => {
  const a = CENSO_ALERTA[alerta];
  if (!a) return null;
  return (
    <Box
      component="span"
      title={a.largo}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        height: 20,
        padding: "0 8px 0 6px",
        borderRadius: 999,
        fontSize: 10.5,
        fontWeight: 600,
        whiteSpace: "nowrap",
        bgcolor: `rgba(var(${a.rgb}),0.12)`,
        color: a.c,
        border: `1px solid rgba(var(${a.rgb}),0.3)`,
      }}
    >
      <CensoIcon name={a.icon} size={12} />
      {corto ? a.label : a.largo}
    </Box>
  );
};

const MESES_CORTOS = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

// "04 oct · 08:10" en hora de Chile (el backend manda UTC). Vacío si la fecha no parsea.
export const censoFecha = (iso) => {
  const d = new Date(iso);
  if (!iso || Number.isNaN(d.getTime())) return "";
  const partes = Object.fromEntries(
    new Intl.DateTimeFormat("en-GB", {
      timeZone: "America/Santiago",
      day: "2-digit",
      month: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    })
      .formatToParts(d)
      .map((p) => [p.type, p.value]),
  );
  return `${partes.day} ${MESES_CORTOS[Number(partes.month) - 1]} · ${partes.hour}:${partes.minute}`;
};

// Iniciales del avatar desde el correo (el panel de censo no recibe el nombre: R11).
export const censoIniciales = (email) => {
  const local = String(email || "").split("@")[0];
  const partes = local.split(/[._-]+/).filter(Boolean);
  if (partes.length >= 2) return (partes[0][0] + partes[1][0]).toUpperCase();
  return (local.slice(0, 2) || "??").toUpperCase();
};

export const CensoAvatar = ({ email, size = 30 }) => (
  <Box
    component="span"
    sx={{
      width: size,
      height: size,
      borderRadius: "50%",
      bgcolor: "rgba(var(--color-primary-rgb),0.22)",
      color: "var(--color-info)",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      fontWeight: 700,
      fontSize: size >= 36 ? 12 : 11,
      flex: "none",
    }}
  >
    {censoIniciales(email)}
  </Box>
);
